/**
 * Vardiya Atama / Düzenleme Modal'ı.
 * İki mod: "Vardiya Ata" veya "İzinli İşaretle".
 */

import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Trash2, Briefcase, Sun } from 'lucide-react'
import {
  vardiyaOlustur,
  vardiyaGuncelle,
  vardiyaSil,
  type Vardiya,
} from '@/lib/services/vardiya-service'
import { kalipSaati, type VardiyaKalibi } from '@/lib/services/vardiya-kalibi-service'
import { useAuth } from '@/contexts/AuthContext'
import type { KullaniciPersonel, Pozisyon } from '@/types'
import { tarihParse } from '@/lib/tarih'

interface Props {
  acik: boolean
  mevcutVardiya: Vardiya | null
  personel: KullaniciPersonel
  subeId: string
  tarih: string
  kaliplar: VardiyaKalibi[]
  pozisyonlar: Pozisyon[]
  onKapat: () => void
  onKaydedildi: () => void
}

type Mod = 'vardiya' | 'izin'

interface FormState {
  mod: Mod
  kalipId: string
  baslangic: string
  bitis: string
  pozisyonId: string
  notlar: string
}

const BOS: FormState = {
  mod: 'vardiya',
  kalipId: '',
  baslangic: '08:00',
  bitis: '18:00',
  pozisyonId: '',
  notlar: '',
}

export function VardiyaAtaModal({
  acik, mevcutVardiya, personel, subeId, tarih,
  kaliplar, pozisyonlar, onKapat, onKaydedildi,
}: Props) {
  const { firebaseUser } = useAuth()
  const [form, setForm] = useState<FormState>(BOS)
  const [hata, setHata] = useState<string | null>(null)
  const [kaydediliyor, setKaydediliyor] = useState(false)
  const [silOnayi, setSilOnayi] = useState(false)

  useEffect(() => {
    if (!acik) return
    if (mevcutVardiya) {
      setForm({
        mod: mevcutVardiya.durum === 'izinli' ? 'izin' : 'vardiya',
        kalipId: mevcutVardiya.kalipId ?? '',
        baslangic: mevcutVardiya.baslangic,
        bitis: mevcutVardiya.bitis,
        pozisyonId: mevcutVardiya.pozisyonId ?? '',
        notlar: mevcutVardiya.notlar ?? '',
      })
    } else {
      setForm(BOS)
    }
    setHata(null)
    setSilOnayi(false)
    setKaydediliyor(false)
  }, [mevcutVardiya, acik])

  function kalipSec(kalipId: string) {
    if (!kalipId) {
      setForm((f) => ({ ...f, kalipId: '' }))
      return
    }
    const kalip = kaliplar.find((k) => k.id === kalipId)
    if (!kalip) return
    setForm((f) => ({
      ...f,
      kalipId,
      baslangic: kalip.baslangic,
      bitis: kalip.bitis,
      pozisyonId: kalip.pozisyonId ?? '',
    }))
  }

  async function kaydet(ev: React.FormEvent) {
    ev.preventDefault()
    if (!firebaseUser) {
      setHata('Oturum bulunamadı.')
      return
    }
    setKaydediliyor(true)
    setHata(null)

    try {
      const izinMi = form.mod === 'izin'

      const data = {
        baslangic: izinMi ? '00:00' : form.baslangic,
        bitis: izinMi ? '00:00' : form.bitis,
        kalipId: izinMi ? undefined : (form.kalipId || undefined),
        pozisyonId: izinMi ? undefined : (form.pozisyonId || undefined),
        notlar: form.notlar.trim() || undefined,
        durum: izinMi ? ('izinli' as const) : ('planli' as const),
      }

      if (mevcutVardiya) {
        await vardiyaGuncelle(mevcutVardiya.id, data)
      } else {
        await vardiyaOlustur(
          {
            personelId: personel.id,
            subeId,
            tarih,
            ...data,
            yayinlandi: false,
          },
          firebaseUser.uid
        )
      }
      onKaydedildi()
    } catch (err) {
      console.error(err)
      setHata('Kaydedilemedi: ' + (err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  async function sil() {
    if (!mevcutVardiya) return
    if (!silOnayi) {
      setSilOnayi(true)
      return
    }
    setKaydediliyor(true)
    setHata(null)
    try {
      await vardiyaSil(mevcutVardiya.id)
      onKaydedildi()
    } catch (err) {
      console.error(err)
      setHata('Silinemedi: ' + (err as Error).message)
    } finally {
      setKaydediliyor(false)
    }
  }

  const subeKaliplari = kaliplar.filter((k) => k.subeId === subeId && k.aktif)
  const personelPozisyonlari = pozisyonlar.filter((p) =>
    personel.pozisyonIdleri.includes(p.id)
  )

  const tarihObj = tarihParse(tarih)
  const tarihEtiketi = tarihObj.toLocaleDateString('tr-TR', {
    weekday: 'long', day: 'numeric', month: 'long',
  })

  const sure = kalipSaati({ baslangic: form.baslangic, bitis: form.bitis })

  return (
    <Modal
      acik={acik}
      onKapat={onKapat}
      baslik={mevcutVardiya ? 'Düzenle' : 'Yeni Atama'}
      boyut="md"
    >
      <form onSubmit={kaydet} className="space-y-4">
        <div className="bg-brand-50 border border-brand-200 rounded-xl p-3 text-sm">
          <p className="font-semibold text-brand-900">{personel.isim}</p>
          <p className="text-brand-700 text-xs mt-0.5">{tarihEtiketi}</p>
        </div>

        {/* Mod seçici */}
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, mod: 'vardiya' }))}
            className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${
              form.mod === 'vardiya'
                ? 'border-brand-600 bg-brand-50 text-brand-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <Briefcase className="w-5 h-5" />
            <span className="text-sm font-semibold">Vardiya</span>
          </button>
          <button
            type="button"
            onClick={() => setForm((f) => ({ ...f, mod: 'izin' }))}
            className={`p-3 rounded-xl border-2 flex flex-col items-center gap-1 transition-colors ${
              form.mod === 'izin'
                ? 'border-amber-500 bg-amber-50 text-amber-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300'
            }`}
          >
            <Sun className="w-5 h-5" />
            <span className="text-sm font-semibold">İzinli</span>
          </button>
        </div>

        {form.mod === 'vardiya' && (
          <>
            {subeKaliplari.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Hızlı Kalıp Seç
                </label>
                <select
                  value={form.kalipId}
                  onChange={(e) => kalipSec(e.target.value)}
                  className="input-base"
                >
                  <option value="">— Manuel —</option>
                  {subeKaliplari.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.isim} ({k.baslangic} - {k.bitis})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Başlangıç
                </label>
                <input
                  type="time"
                  value={form.baslangic}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, baslangic: e.target.value, kalipId: '' }))
                  }
                  className="input-base"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Bitiş
                </label>
                <input
                  type="time"
                  value={form.bitis}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, bitis: e.target.value, kalipId: '' }))
                  }
                  className="input-base"
                />
              </div>
            </div>

            {sure > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-2 text-xs text-blue-900 text-center">
                ⏱️ Vardiya süresi: <strong>{sure} saat</strong>
                {form.bitis.localeCompare(form.baslangic) <= 0 && ' (gece)'}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Pozisyon (opsiyonel)
              </label>
              <select
                value={form.pozisyonId}
                onChange={(e) => setForm((f) => ({ ...f, pozisyonId: e.target.value }))}
                className="input-base"
              >
                <option value="">— Belirtilmemiş —</option>
                {personelPozisyonlari.map((p) => (
                  <option key={p.id} value={p.id}>{p.isim}</option>
                ))}
              </select>
              {personelPozisyonlari.length === 0 && (
                <p className="text-xs text-amber-700 mt-1">
                  Bu personelin atanmış pozisyonu yok.
                </p>
              )}
            </div>
          </>
        )}

        {form.mod === 'izin' && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
            ☀️ Bu gün <strong>izinli</strong> olarak işaretlenecek. Personel bu vardiyayı görebilir.
            <p className="text-xs mt-1 text-amber-800">
              Not: İzin türü (yıllık / aylık) ve maaş etkisi Faz 4'te detaylanacak.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Not (opsiyonel)
          </label>
          <input
            type="text"
            value={form.notlar}
            onChange={(e) => setForm((f) => ({ ...f, notlar: e.target.value }))}
            className="input-base"
            placeholder={form.mod === 'izin' ? 'Örn: Doktor randevusu' : 'Örn: Mal kabul yapacak'}
          />
        </div>

        {mevcutVardiya?.yayinlandi && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900">
            ⚠️ Bu kayıt yayınlanmış, personel görüyor. Değişiklik yaparsan personele tekrar bildirilmesi gerekebilir.
          </div>
        )}

        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          {mevcutVardiya && (
            <button
              type="button"
              onClick={sil}
              disabled={kaydediliyor}
              className={`px-3 py-2 rounded-xl flex items-center gap-1 transition-colors ${
                silOnayi
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-50 text-red-700 hover:bg-red-100'
              }`}
            >
              <Trash2 className="w-4 h-4" />
              <span className="text-sm">{silOnayi ? 'Eminim, sil' : 'Sil'}</span>
            </button>
          )}
          <button
            type="button"
            onClick={onKapat}
            className="btn-secondary flex-1"
            disabled={kaydediliyor}
          >
            İptal
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={kaydediliyor}>
            {kaydediliyor ? 'Kaydediliyor...' : mevcutVardiya ? 'Güncelle' : 'Ekle'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
